import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import "./CustomDevelopmentPageNavigation.css";

const CUSTOM_DEVELOPMENT_NAVIGATION_ITEMS = [
  {
    id: "challenge",
    label: "The Challenge",
    href: "#custom-development-challenge",
    targetSelector: "#custom-development-challenge",
  },
  {
    id: "system",
    label: "What We Build",
    href: "#custom-development-what-we-build",
    targetSelector: "#custom-development-what-we-build",
  },
  {
    id: "discovery",
    label: "Discovery",
    href: "#custom-development-discovery",
    targetSelector: "#custom-development-discovery",
  },
  {
    id: "transformation",
    label: "Transformation",
    href: "#custom-development-transformation",
    targetSelector: "#custom-development-transformation",
  },
  {
    id: "delivery",
    label: "How We Work",
    href: "#custom-development-how-we-work",
    targetSelector: "#custom-development-how-we-work",
  },
  {
    id: "governance",
    label: "Governance",
    href: "#custom-development-governance",
    targetSelector: "#custom-development-governance",
  },
  {
    id: "start-project",
    label: "Start a Project",
    href: "#custom-development-engagement",
    targetSelector: "#custom-development-engagement",
    primary: true,
  },
];

const CUSTOM_DEVELOPMENT_TOP_TARGET = {
  href: "#custom-development-top",
  targetSelector: "#custom-development-top",
};

const CUSTOM_DEVELOPMENT_DEFAULT_NAVIGATION_ID =
  CUSTOM_DEVELOPMENT_NAVIGATION_ITEMS[0].id;

const CUSTOM_DEVELOPMENT_NAVIGATION_GAP = 12;
const CUSTOM_DEVELOPMENT_MANUAL_SELECTION_DURATION = 1400;

function customDevelopmentPrefersReducedMotion() {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches
  );
}

function isModifiedCustomDevelopmentNavigationEvent(
  event
) {
  return (
    event.defaultPrevented ||
    event.button !== 0 ||
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey
  );
}

export default function CustomDevelopmentPageNavigation() {
  const navigationRef = useRef(null);
  const navigationRailRef = useRef(null);
  const navigationLinkRefs = useRef({});
  const manualNavigationRef = useRef(null);

  const [activeNavigationId, setActiveNavigationId] =
    useState(
      CUSTOM_DEVELOPMENT_DEFAULT_NAVIGATION_ID
    );

  /*
   * Detect the primary AndUs Navbar if it is fixed or sticky and
   * position this page navigation directly beneath it.
   */
  useEffect(() => {
    const navigationElement = navigationRef.current;

    if (!navigationElement) {
      return undefined;
    }

    const getExternalPinnedNavigationElements = () =>
      Array.from(
        document.querySelectorAll("header, nav")
      ).filter((element) => {
        if (
          element === navigationElement ||
          navigationElement.contains(element)
        ) {
          return false;
        }

        const styles = window.getComputedStyle(element);
        const rect = element.getBoundingClientRect();

        const isPinned =
          styles.position === "fixed" ||
          styles.position === "sticky";

        const isVisible =
          styles.display !== "none" &&
          styles.visibility !== "hidden" &&
          rect.height > 0 &&
          rect.bottom > 0 &&
          rect.top <= 8;

        return isPinned && isVisible;
      });

    const updateStickyPosition = () => {
      const pinnedNavigationBottom =
        getExternalPinnedNavigationElements().reduce(
          (currentBottom, element) => {
            const rect = element.getBoundingClientRect();

            return Math.max(currentBottom, rect.bottom);
          },
          0
        );

      navigationElement.style.setProperty(
        "--custom-development-page-navigation-sticky-top",
        `${Math.max(
          0,
          Math.round(pinnedNavigationBottom)
        )}px`
      );
    };

    updateStickyPosition();

    window.addEventListener(
      "resize",
      updateStickyPosition
    );

    let navigationResizeObserver;

    if (typeof ResizeObserver !== "undefined") {
      navigationResizeObserver = new ResizeObserver(
        updateStickyPosition
      );

      getExternalPinnedNavigationElements().forEach(
        (element) => {
          navigationResizeObserver.observe(element);
        }
      );
    }

    return () => {
      window.removeEventListener(
        "resize",
        updateStickyPosition
      );

      navigationResizeObserver?.disconnect();
    };
  }, []);

  const getNavigationOffset = useCallback(() => {
    const navigationElement = navigationRef.current;

    if (!navigationElement) {
      return 0;
    }

    const styles =
      window.getComputedStyle(navigationElement);

    const navigationTop = Number.parseFloat(styles.top);

    const topOffset = Number.isFinite(navigationTop)
      ? Math.max(0, navigationTop)
      : Math.max(
          0,
          navigationElement.getBoundingClientRect().top
        );

    return (
      topOffset +
      navigationElement.offsetHeight +
      CUSTOM_DEVELOPMENT_NAVIGATION_GAP
    );
  }, []);

  /*
   * Tracks the active high-level stage while the user scrolls.
   *
   * Sections that do not need an individual navigation item remain
   * grouped beneath their nearest high-level stage:
   *
   * - Capabilities remains part of Discovery/Transformation.
   * - Product Proof remains part of Delivery.
   * - Implementation remains part of Governance.
   */
  useEffect(() => {
    let animationFrameId = null;

    const updateActiveNavigation = () => {
      animationFrameId = null;

      const navigationOffset = getNavigationOffset();

      const activationPosition =
        window.scrollY + navigationOffset + 4;

      const availableSections =
        CUSTOM_DEVELOPMENT_NAVIGATION_ITEMS.map(
          (navigationItem) => {
            const sectionElement = document.querySelector(
              navigationItem.targetSelector
            );

            if (!sectionElement) {
              return null;
            }

            return {
              ...navigationItem,
              element: sectionElement,
              top:
                sectionElement.getBoundingClientRect().top +
                window.scrollY,
            };
          }
        ).filter(Boolean);

      if (availableSections.length === 0) {
        return;
      }

      const manualNavigation =
        manualNavigationRef.current;

      if (
        manualNavigation &&
        performance.now() < manualNavigation.expiresAt
      ) {
        const selectedSection = availableSections.find(
          (section) =>
            section.id === manualNavigation.id
        );

        if (selectedSection) {
          const selectedSectionViewportTop =
            selectedSection.element.getBoundingClientRect().top;

          const hasReachedSelectedSection =
            Math.abs(
              selectedSectionViewportTop - navigationOffset
            ) <= 24;

          if (!hasReachedSelectedSection) {
            setActiveNavigationId((currentId) =>
              currentId === manualNavigation.id
                ? currentId
                : manualNavigation.id
            );

            return;
          }
        }

        manualNavigationRef.current = null;
      } else if (manualNavigation) {
        manualNavigationRef.current = null;
      }

      let nextActiveNavigationId =
        availableSections[0].id;

      availableSections.forEach((section) => {
        if (section.top <= activationPosition) {
          nextActiveNavigationId = section.id;
        }
      });

      setActiveNavigationId((currentId) =>
        currentId === nextActiveNavigationId
          ? currentId
          : nextActiveNavigationId
      );
    };

    const requestNavigationUpdate = () => {
      if (animationFrameId !== null) {
        return;
      }

      animationFrameId = window.requestAnimationFrame(
        updateActiveNavigation
      );
    };

    updateActiveNavigation();

    window.addEventListener(
      "scroll",
      requestNavigationUpdate,
      { passive: true }
    );

    window.addEventListener(
      "resize",
      requestNavigationUpdate
    );

    window.addEventListener(
      "hashchange",
      requestNavigationUpdate
    );

    return () => {
      window.removeEventListener(
        "scroll",
        requestNavigationUpdate
      );

      window.removeEventListener(
        "resize",
        requestNavigationUpdate
      );

      window.removeEventListener(
        "hashchange",
        requestNavigationUpdate
      );

      if (animationFrameId !== null) {
        window.cancelAnimationFrame(animationFrameId);
      }
    };
  }, [getNavigationOffset]);

  /*
   * Keep the active link visible inside the horizontal navigation rail.
   */
  useEffect(() => {
    const navigationRail = navigationRailRef.current;

    const activeLink =
      navigationLinkRefs.current[activeNavigationId];

    if (
      !navigationRail ||
      !activeLink ||
      navigationRail.scrollWidth <=
        navigationRail.clientWidth
    ) {
      return;
    }

    const railVisibleStart =
      navigationRail.scrollLeft + 12;

    const railVisibleEnd =
      navigationRail.scrollLeft +
      navigationRail.clientWidth -
      12;

    const activeLinkStart = activeLink.offsetLeft;

    const activeLinkEnd =
      activeLink.offsetLeft + activeLink.offsetWidth;

    let nextScrollLeft = navigationRail.scrollLeft;

    if (activeLinkStart < railVisibleStart) {
      nextScrollLeft = activeLinkStart - 18;
    } else if (activeLinkEnd > railVisibleEnd) {
      nextScrollLeft =
        activeLinkEnd -
        navigationRail.clientWidth +
        18;
    }

    const maximumScrollLeft = Math.max(
      0,
      navigationRail.scrollWidth -
        navigationRail.clientWidth
    );

    nextScrollLeft = Math.min(
      maximumScrollLeft,
      Math.max(0, nextScrollLeft)
    );

    if (
      Math.abs(
        nextScrollLeft - navigationRail.scrollLeft
      ) > 1
    ) {
      navigationRail.scrollTo({
        left: nextScrollLeft,
        behavior:
          customDevelopmentPrefersReducedMotion()
            ? "auto"
            : "smooth",
      });
    }
  }, [activeNavigationId]);

  const handleNavigationTarget = (
    event,
    destination,
    nextActiveNavigationId
  ) => {
    if (
      isModifiedCustomDevelopmentNavigationEvent(
        event
      )
    ) {
      return;
    }

    const targetElement = document.querySelector(
      destination.targetSelector
    );

    if (!targetElement) {
      return;
    }

    event.preventDefault();

    if (nextActiveNavigationId) {
      manualNavigationRef.current = {
        id: nextActiveNavigationId,
        expiresAt:
          performance.now() +
          CUSTOM_DEVELOPMENT_MANUAL_SELECTION_DURATION,
      };

      setActiveNavigationId(nextActiveNavigationId);
    } else {
      manualNavigationRef.current = null;
    }

    const navigationOffset = getNavigationOffset();

    const targetScrollPosition =
      targetElement.getBoundingClientRect().top +
      window.scrollY -
      navigationOffset;

    if (window.location.hash !== destination.href) {
      window.history.replaceState(
        null,
        "",
        destination.href
      );
    }

    window.scrollTo({
      top: Math.max(
        0,
        Math.round(targetScrollPosition)
      ),
      behavior:
        customDevelopmentPrefersReducedMotion()
          ? "auto"
          : "smooth",
    });
  };

  return (
    <nav
      ref={navigationRef}
      id="custom-development-page-navigation"
      className="custom-development-page-navigation"
      aria-label="Custom Development page navigation"
    >
      <div className="custom-development-page-navigation__inner">
        <a
          className="custom-development-page-navigation__identity"
          href={CUSTOM_DEVELOPMENT_TOP_TARGET.href}
          aria-label="Return to the top of the Custom Development page"
          onClick={(event) =>
            handleNavigationTarget(
              event,
              CUSTOM_DEVELOPMENT_TOP_TARGET,
              CUSTOM_DEVELOPMENT_DEFAULT_NAVIGATION_ID
            )
          }
        >
          <span className="custom-development-page-navigation__identity-eyebrow">
            Custom Development
          </span>

          <strong className="custom-development-page-navigation__identity-title">
            Explore the page
          </strong>
        </a>

        <div
          ref={navigationRailRef}
          className="custom-development-page-navigation__scroll"
        >
          {CUSTOM_DEVELOPMENT_NAVIGATION_ITEMS.map(
            (navigationItem) => {
              const isActive =
                activeNavigationId === navigationItem.id;

              return (
                <a
                  key={navigationItem.id}
                  ref={(linkElement) => {
                    if (linkElement) {
                      navigationLinkRefs.current[
                        navigationItem.id
                      ] = linkElement;
                    } else {
                      delete navigationLinkRefs.current[
                        navigationItem.id
                      ];
                    }
                  }}
                  className={[
                    "custom-development-page-navigation__link",
                    navigationItem.primary
                      ? "custom-development-page-navigation__link--primary"
                      : "",
                    isActive
                      ? "custom-development-page-navigation__link--active"
                      : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  href={navigationItem.href}
                  aria-current={
                    isActive ? "location" : undefined
                  }
                  onClick={(event) =>
                    handleNavigationTarget(
                      event,
                      navigationItem,
                      navigationItem.id
                    )
                  }
                >
                  <span
                    className="custom-development-page-navigation__link-indicator"
                    aria-hidden="true"
                  />

                  <span className="custom-development-page-navigation__link-label">
                    {navigationItem.label}
                  </span>
                </a>
              );
            }
          )}
        </div>
      </div>
    </nav>
  );
}